import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, ArrowLeft, ArrowRight, X, Camera } from "lucide-react";

const wildlifePhotos = [
  {
    src: "https://customer-assets.emergentagent.com/job_portfolio-showcase-2863/artifacts/6ondvh1g_DSCN5703%201.png",
    caption: "Common Jezebel · Puddling",
    location: "Buxa Tiger Reserve",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_portfolio-showcase-2863/artifacts/3vc9v7dk_DSCN6295%201.png",
    caption: "Great Hornbill",
    location: "Latpanchar, West Bengal",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_portfolio-showcase-2863/artifacts/le7rslj9_011%201.png",
    caption: "Paris Peacock Butterfly",
    location: "Neora Valley",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_portfolio-showcase-2863/artifacts/eeypnghx_172078580_3394287070670973_4211854560946077818_n%201.png",
    caption: "Nawab Butterfly",
    location: "Buxa Tiger Reserve",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_portfolio-showcase-2863/artifacts/n32zk7jh_2%2038191374.png",
    caption: "Indian Elephant",
    location: "Jaldapara National Park",
  },
];

const chapters = [
  {
    id: "wildlife",
    no: "01",
    title: "Wildlife Photography",
    body: "Great Hornbill. Indian Peacock. Jezebel and Paris Peacock butterflies. The forest teaches patience more than any UX class.",
    image: "https://customer-assets.emergentagent.com/job_portfolio-showcase-2863/artifacts/iu0nwuhu_Wild%20life.jpg",
    tag: "Lens / Field",
    album: wildlifePhotos,
  },
  {
    id: "karate",
    no: "02",
    title: "Karate",
    body: "Form before force. Discipline before display. The same rules apply to a typographic system as a kata.",
    image: "https://customer-assets.emergentagent.com/job_portfolio-showcase-2863/artifacts/vt6bnf49_Karate.JPG",
    tag: "Dojo / Practice",
  },
  {
    id: "kitchen",
    no: "03",
    title: "Home Cook",
    body: "Simple ingredients, slow heat, restraint. A good plate is a good interface: you taste the work, not the wiring.",
    image: "https://customer-assets.emergentagent.com/job_portfolio-showcase-2863/artifacts/bbswunax_Home%20cook.png",
    tag: "Kitchen / Slow",
  },
  {
    id: "travel",
    no: "04",
    title: "Travel",
    body: "I travel to collect cultural inspiration, then bring those colours, scripts and silences back into the design work.",
    image: "https://customer-assets.emergentagent.com/job_portfolio-showcase-2863/artifacts/0ysdm6co_Travel.JPG",
    tag: "World / Notes",
  },
];

const timeline = [
  { year: "2024", role: "UX Designer", place: "Nunify · Bangalore" },
  { year: "2024", role: "PG, User Experience Design", place: "University of Texas" },
  { year: "2022 / 2024", role: "UX / UI Designer", place: "Provus · California (via 2DOT47)" },
  { year: "2022", role: "UX / UI Designer", place: "2DOT47 · Pune" },
  { year: "2022", role: "Product Design Intern", place: "Itilite · Bangalore" },
  { year: "2022", role: "UI / UX Design", place: "Designerrs Academy" },
  { year: "2021", role: "Foundations of UI / UX", place: "Google via Coursera" },
  { year: "2019 / 2020", role: "Computer Instructor", place: "Saha Multimedia · Kolkata" },
  { year: "2015", role: "B.A.", place: "Bhairab Ganguly College, WBSU" },
];

const certifications = [
  { title: "Post Graduate, UX Design", at: "University of Texas", year: "2024" },
  { title: "UI / UX Design", at: "Designerrs Academy", year: "2022" },
  { title: "Foundations of UI / UX", at: "Google via Coursera", year: "2021" },
];

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: [0.65, 0, 0.35, 1], delay: i * 0.08 },
  }),
};

const Lightbox = ({ photos, activeIndex, onClose, onPrev, onNext }) => {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") onPrev();
      else if (e.key === "ArrowRight") onNext();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose, onPrev, onNext]);

  const photo = photos[activeIndex];

  return (
    <motion.div
      data-testid="wildlife-lightbox"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      className="fixed inset-0 z-[100] bg-[#0A0A0A]/95 backdrop-blur-md flex flex-col"
      onClick={onClose}
    >
      {/* Top bar */}
      <div className="flex items-center justify-between px-6 md:px-10 py-5 md:py-6 text-[#F4F4F2]">
        <div className="font-mono-mini text-[#F4F4F2]/70">
          Wildlife Album · {String(activeIndex + 1).padStart(2, "0")} / {String(photos.length).padStart(2, "0")}
        </div>
        <button
          type="button"
          data-testid="lightbox-close"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          className="group inline-flex items-center gap-2 font-mono-mini"
        >
          <span className="link-underline">Close</span>
          <X size={16} className="transition-transform duration-300 group-hover:rotate-90" />
        </button>
      </div>

      {/* Photo stage */}
      <div
        className="flex-1 flex items-center justify-center px-4 md:px-10 pb-4 md:pb-6"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          data-testid="lightbox-prev"
          onClick={onPrev}
          aria-label="Previous photo"
          className="hidden md:inline-flex items-center justify-center w-12 h-12 rounded-full border border-white/25 text-[#F4F4F2] hover:bg-white/10 transition-colors duration-300 mr-6"
        >
          <ArrowLeft size={20} />
        </button>

        <AnimatePresence mode="wait">
          <motion.figure
            key={photo.src}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.45, ease: [0.65, 0, 0.35, 1] }}
            className="flex-1 flex flex-col items-center justify-center max-h-full"
          >
            <img
              src={photo.src}
              alt={photo.caption}
              data-testid={`lightbox-photo-${activeIndex}`}
              className="max-h-[70vh] md:max-h-[75vh] max-w-full object-contain"
            />
            <figcaption className="mt-4 md:mt-6 flex items-baseline gap-4 text-[#F4F4F2]">
              <span className="font-display text-lg md:text-2xl tracking-tighter">{photo.caption}</span>
              <span className="font-mono-mini text-[#F4F4F2]/60">{photo.location}</span>
            </figcaption>
          </motion.figure>
        </AnimatePresence>

        <button
          type="button"
          data-testid="lightbox-next"
          onClick={onNext}
          aria-label="Next photo"
          className="hidden md:inline-flex items-center justify-center w-12 h-12 rounded-full border border-white/25 text-[#F4F4F2] hover:bg-white/10 transition-colors duration-300 ml-6"
        >
          <ArrowRight size={20} />
        </button>
      </div>

      {/* Bottom controls (mobile) + thumbnails */}
      <div
        className="px-4 md:px-10 pb-6 md:pb-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex md:hidden items-center justify-center gap-4 mb-4">
          <button
            type="button"
            onClick={onPrev}
            aria-label="Previous"
            className="inline-flex items-center justify-center w-11 h-11 rounded-full border border-white/25 text-[#F4F4F2]"
          >
            <ArrowLeft size={18} />
          </button>
          <button
            type="button"
            onClick={onNext}
            aria-label="Next"
            className="inline-flex items-center justify-center w-11 h-11 rounded-full border border-white/25 text-[#F4F4F2]"
          >
            <ArrowRight size={18} />
          </button>
        </div>
        <div className="flex items-center justify-center gap-2 md:gap-3">
          {photos.map((p, i) => (
            <button
              key={p.src}
              type="button"
              data-testid={`lightbox-thumb-${i}`}
              onClick={() => onPrev(i, true)}
              aria-label={`Go to photo ${i + 1}`}
              className={`h-1.5 rounded-full transition-all duration-500 ${
                i === activeIndex ? "w-10 bg-[#F4F4F2]" : "w-6 bg-[#F4F4F2]/25 hover:bg-[#F4F4F2]/50"
              }`}
            />
          ))}
        </div>
      </div>
    </motion.div>
  );
};

const ChapterCard = ({ c, i, onOpenAlbum }) => {
  const hasAlbum = !!c.album?.length;

  return (
    <motion.article
      variants={fade}
      custom={i}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, margin: "-60px" }}
      data-testid={`about-chapter-${c.id}`}
      className={`group relative bg-white border border-black/10 overflow-hidden flex flex-col ${
        hasAlbum ? "cursor-pointer" : ""
      }`}
      onClick={hasAlbum ? onOpenAlbum : undefined}
      onKeyDown={
        hasAlbum
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onOpenAlbum();
              }
            }
          : undefined
      }
      role={hasAlbum ? "button" : undefined}
      tabIndex={hasAlbum ? 0 : undefined}
    >
      <div className="relative h-64 md:h-72 overflow-hidden bg-[#EFEEEC]">
        <img
          src={c.image}
          alt={c.title}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.06]"
        />
        <div className="absolute top-4 left-4 right-4 flex items-start justify-between font-mono-mini text-white mix-blend-difference">
          <span>{c.no}</span>
          <span>{c.tag}</span>
        </div>
        {hasAlbum && (
          <div
            data-testid={`about-chapter-${c.id}-album-badge`}
            className="absolute bottom-4 left-4 right-4 flex items-center justify-between text-white mix-blend-difference"
          >
            <span className="inline-flex items-center gap-2 font-mono-mini">
              <Camera size={12} />
              {String(c.album.length).padStart(2, "0")} photos
            </span>
            <span className="inline-flex items-center gap-1 font-mono-mini opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              View album <ArrowUpRight size={12} />
            </span>
          </div>
        )}
      </div>
      <div className="p-5 md:p-6 flex-1 flex flex-col">
        <h3 className="font-display text-xl md:text-2xl tracking-tighter leading-tight">
          {c.title}
        </h3>
        <p className="mt-3 text-sm md:text-[15px] leading-relaxed text-[#0A0A0A]/80">
          {c.body}
        </p>
      </div>
    </motion.article>
  );
};

export const About = () => {
  const [albumOpen, setAlbumOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const openAlbum = () => {
    setActiveIndex(0);
    setAlbumOpen(true);
  };
  const closeAlbum = () => setAlbumOpen(false);
  const prev = (i, isThumb) => {
    if (isThumb && typeof i === "number") {
      setActiveIndex(i);
    } else {
      setActiveIndex((cur) => (cur - 1 + wildlifePhotos.length) % wildlifePhotos.length);
    }
  };
  const next = () => setActiveIndex((cur) => (cur + 1) % wildlifePhotos.length);

  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative border-t border-black/10 py-24 md:py-32 lg:py-40 bg-[#F4F4F2]"
    >
      <div className="max-w-[1500px] mx-auto px-6 md:px-12">
        {/* Section meta */}
        <div className="grid grid-cols-12 gap-6 md:gap-12 mb-16 md:mb-24">
          <div className="col-span-12 md:col-span-3">
            <div className="font-mono-mini text-[#8A8A8A]">§ 03</div>
            <div className="font-mono-mini text-[#0A0A0A] mt-1">About</div>
          </div>
          <div className="col-span-12 md:col-span-8 md:col-start-5">
            <motion.h2
              data-testid="about-heading"
              variants={fade}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-100px" }}
              className="font-display text-4xl md:text-6xl lg:text-7xl tracking-tighter leading-[0.95]"
            >
              Designer by day.{" "}
              <span
                className="italic font-light"
                style={{ fontFamily: "'Cabinet Grotesk', serif" }}
              >
                Naturalist by weekend.
              </span>
            </motion.h2>
          </div>
        </div>

        {/* Bio block: editorial pull-paragraph + index card */}
        <div className="grid grid-cols-12 gap-6 md:gap-12 mb-24 md:mb-32">
          <motion.div
            variants={fade}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            className="col-span-12 md:col-span-7 border-t border-black/15 pt-8"
          >
            <span
              aria-hidden
              className="font-display text-[6rem] leading-none text-[#FF3B30] inline-block -mt-6 mr-2 align-top select-none"
            >
              “
            </span>
            <p
              data-testid="about-bio"
              className="inline font-display text-2xl md:text-3xl lg:text-[2.1rem] leading-[1.25] tracking-tight text-[#0A0A0A]"
            >
              I&apos;m a UI / UX Designer at Nunify who loves blending technology
              with creativity. Outside of design I shoot wildlife on quiet
              mornings, practise karate, and travel to collect cultural
              inspiration. I cook because it teaches restraint. I connect
              design, nature and culture to keep the work feeling alive.
            </p>
          </motion.div>

          <motion.aside
            variants={fade}
            custom={1}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            data-testid="about-index-card"
            className="col-span-12 md:col-span-4 md:col-start-9 border border-black/15 p-6 md:p-8 self-start"
          >
            <div className="font-mono-mini text-[#8A8A8A]">Index Card</div>
            <dl className="mt-6 grid grid-cols-3 gap-y-5 gap-x-4">
              <dt className="col-span-1 font-mono-mini text-[#8A8A8A]">Born</dt>
              <dd className="col-span-2 text-sm md:text-base font-medium">West Bengal, India</dd>

              <dt className="col-span-1 font-mono-mini text-[#8A8A8A]">Based</dt>
              <dd className="col-span-2 text-sm md:text-base font-medium">Bangalore, IN</dd>

              <dt className="col-span-1 font-mono-mini text-[#8A8A8A]">Day</dt>
              <dd className="col-span-2 text-sm md:text-base font-medium">UX Designer, Nunify</dd>

              <dt className="col-span-1 font-mono-mini text-[#8A8A8A]">Off-hours</dt>
              <dd className="col-span-2 text-sm md:text-base font-medium">Lens. Dojo. Kitchen. Trail.</dd>

              <dt className="col-span-1 font-mono-mini text-[#8A8A8A]">Stack</dt>
              <dd className="col-span-2 text-sm md:text-base font-medium">Figma · Affinity · Adobe CC · After Effects</dd>
            </dl>
            <a
              href="https://customer-assets.emergentagent.com/job_portfolio-showcase-2863/artifacts/sy01uftu_MM.Resume.pdf"
              target="_blank"
              rel="noreferrer"
              data-testid="about-resume-link"
              className="mt-8 inline-flex items-center gap-2 font-mono-mini text-[#0A0A0A] group"
            >
              <span className="link-underline-static">Download Résumé</span>
              <ArrowUpRight
                size={14}
                className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1"
              />
            </a>
          </motion.aside>
        </div>

        {/* OFF THE SCREEN — four chapters */}
        <div className="mb-12 md:mb-16 flex items-baseline justify-between border-t border-black/15 pt-6">
          <div className="font-mono-mini text-[#0A0A0A]">Off the screen</div>
          <div className="font-mono-mini text-[#8A8A8A] hidden md:block">04 chapters</div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 mb-24 md:mb-32">
          {chapters.map((c, i) => (
            <ChapterCard
              key={c.id}
              c={c}
              i={i}
              onOpenAlbum={openAlbum}
            />
          ))}
        </div>

        {/* PATH SO FAR — timeline */}
        <div className="mb-10 md:mb-14 flex items-baseline justify-between border-t border-black/15 pt-6">
          <div className="font-mono-mini text-[#0A0A0A]">Path so far</div>
          <div className="font-mono-mini text-[#8A8A8A] hidden md:block">2015 / present</div>
        </div>

        <ol data-testid="about-timeline" className="border-t border-black/15">
          {timeline.map((t, i) => (
            <motion.li
              key={`${t.year}-${i}`}
              variants={fade}
              custom={i * 0.5}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-40px" }}
              className="group grid grid-cols-12 gap-4 md:gap-12 border-b border-black/10 py-6 md:py-7 transition-colors duration-300 hover:bg-white"
            >
              <div className="col-span-4 md:col-span-2 font-mono-mini text-[#8A8A8A]">
                {t.year}
              </div>
              <div className="col-span-8 md:col-span-5 font-display text-xl md:text-2xl tracking-tighter">
                {t.role}
              </div>
              <div className="col-span-12 md:col-span-5 text-sm md:text-base text-[#0A0A0A]/70 md:text-right">
                {t.place}
              </div>
            </motion.li>
          ))}
        </ol>

        {/* CERTIFICATIONS strip */}
        <div className="mt-16 md:mt-24 grid grid-cols-12 gap-6 md:gap-12">
          <div className="col-span-12 md:col-span-3 font-mono-mini text-[#8A8A8A]">
            Certifications
          </div>
          <div className="col-span-12 md:col-span-9 grid grid-cols-1 md:grid-cols-3 border-t border-black/15">
            {certifications.map((c, i) => (
              <div
                key={c.title}
                data-testid={`about-cert-${i}`}
                className={`py-6 md:py-8 ${
                  i < 2 ? "md:border-r border-black/15" : ""
                } border-b md:border-b-0 border-black/15`}
              >
                <div className="font-mono-mini text-[#8A8A8A]">{c.year}</div>
                <div className="mt-2 font-display text-lg md:text-xl tracking-tighter">
                  {c.title}
                </div>
                <div className="mt-1 text-sm text-[#0A0A0A]/70">{c.at}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <AnimatePresence>
        {albumOpen && (
          <Lightbox
            photos={wildlifePhotos}
            activeIndex={activeIndex}
            onClose={closeAlbum}
            onPrev={prev}
            onNext={next}
          />
        )}
      </AnimatePresence>
    </section>
  );
};
